/**
 * Gallery Wall Bundle - Frame & Variant Management
 * Rewritten for reliability and maintainability
 */

(function($) {
  'use strict';

  // ========================================================================
  // UTILITY FUNCTIONS
  // ========================================================================

  /**
   * Parse integer from string, removing non-numeric characters
   */
  const parseInteger = (value) => {
    return parseInt((value || '').toString().replace(/[^\d\-]/g, ''), 10) || 0;
  };

  /**
   * Parse float from string, removing non-numeric characters except decimal and minus
   */
  const parseNumber = (value) => {
    if (value == null) return 0;
    const str = String(value).replace(/[^\d.-]/g, '');
    const num = parseFloat(str);
    return isNaN(num) ? 0 : num;
  };

  /**
   * Calculate discount percentage
   */
  const calculateDiscount = (price, comparePrice) => {
    return (comparePrice > 0 && price > 0 && price < comparePrice) 
      ? Math.round((1 - price / comparePrice) * 100) 
      : 0;
  };

  /**
   * Normalize string for comparison (lowercase, no whitespace)
   */
  const normalize = (str) => {
    return (str || '').toString().toLowerCase().replace(/\s+/g, '');
  };

  // ========================================================================
  // FRAME VARIANT PICKER FUNCTIONS
  // ========================================================================

  /**
   * Build URL for frame variant AJAX request
   */
  function buildRequestUrl() {
    const $picker = $('frame-variant-picker');
    
    if ($picker.length === 0) {
      return null;
    }

    const sectionId = $picker
      .parents('.shopify-section')
      .attr('id')
      ?.replace('shopify-section-', '');
    
    const $selectedOption = $('frame-variant-picker input:checked');
    const optionValueId = $selectedOption.data('option-value-id');

    if (!optionValueId || !sectionId) {
      return null;
    }

    const productUrl = $picker.data('product-url');
    return `${productUrl}?section_id=${sectionId}&option_values=${optionValueId}&view=frame`;
  }

  /**
   * Ensure the correct frame is checked based on window.selected_frame
   */
  function ensureFrameChecked() {
    if (!window.selected_frame) return;

    const $targetInput = $(`.mrk-frame-switcher input[frame-color="${window.selected_frame}"]`);
    
    if ($targetInput.length > 0 && !$targetInput.prop('checked')) {
      $targetInput.prop('checked', true).trigger('change');
    }
  }

  /**
   * Update frame variants via AJAX
   */
  function updateFrameVariants(selectedOption, callback) {
    // Close any open size selector details
    $('.mrk-size-selector-wrapper details').prop('open', false);

    // Check the matching input in frame-variant-picker
    $('frame-variant-picker input').each(function() {
      if ($(this).val() === selectedOption) {
        $(this).prop('checked', true);
      }
    });

    // Verify the input exists
    if ($(`frame-variant-picker input[value='${selectedOption}']`).length === 0) {
      if (callback) callback();
      return;
    }

    // Store currently selected frame
    window.selected_frame = $('.mrk-frame-switcher input:checked').attr('frame-color');

    const url = buildRequestUrl();
    if (!url) {
      if (callback) callback();
      return;
    }

    // Fetch updated frame variants
    $.get(url, function(response) {
      const variantPickerHtml = $(response).find('frame-variant-picker').html();
      $('frame-variant-picker').html(variantPickerHtml);

      // Wait for DOM to update before re-checking frame
      requestAnimationFrame(() => {
        ensureFrameChecked();
        if (callback) callback();
      });
    }).fail(function() {
      console.error('Failed to update frame variants');
      if (callback) callback();
    });
  }

  /**
   * Find frame variant by size and color with exact matching first
   */
  function findFrameVariant(sizeText, color) {
    const sizeNorm = normalize(sizeText);
    const colorNorm = normalize(color);
    const list = Array.isArray(window.mrkFrameData) ? window.mrkFrameData : [];

    if (list.length === 0) return null;

    // Helper to get options array
    const getOptions = (variant) => {
      if (Array.isArray(variant.options) && variant.options.length) {
        return variant.options;
      }
      return [variant.option1, variant.option2, variant.option3].filter(Boolean);
    };

    // Helper to create variant object
    const createVariant = (v) => ({
      id: v.id,
      price: parseInteger(v.price || v.price_cents),
      compare: parseInteger(v.compare_at_price || v.compare_at_price_cents)
    });

    // PASS 1: Exact match on both size and color
    if (sizeNorm) {
      for (const variant of list) {
        const opts = getOptions(variant);
        const hasExactColor = opts.some(o => normalize(o) === colorNorm);
        const hasExactSize = opts.some(o => normalize(o) === sizeNorm);
        
        if (hasExactColor && hasExactSize) {
          return createVariant(variant);
        }
      }
    }

    // PASS 2: Fuzzy size match with exact color
    if (sizeNorm) {
      for (const variant of list) {
        const opts = getOptions(variant);
        const hasExactColor = opts.some(o => normalize(o) === colorNorm);
        const hasFuzzySize = opts.some(o => {
          const optNorm = normalize(o);
          return optNorm.includes(sizeNorm) || sizeNorm.includes(optNorm);
        });
        
        if (hasExactColor && hasFuzzySize) {
          return createVariant(variant);
        }
      }
    }

    // PASS 3: Color only (fallback)
    for (const variant of list) {
      const opts = getOptions(variant);
      if (opts.some(o => normalize(o) === colorNorm)) {
        return createVariant(variant);
      }
    }

    return null;
  }

  // ========================================================================
  // FRAME TOTAL UPDATES
  // ========================================================================

  /**
   * Update frame pricing for child frame items
   */
  function updateFrameTotals() {
    $('.mrk-child-frame-item').each(function() {
      const $item = $(this);
      let totalPrice = 0;
      let totalCompare = 0;
      const qty = 1;
      const color = ($item.find('input').first().val() || '').trim();

      // Calculate total from all selected variant options
      $('.mrk-variant-option option:selected').each(function() {
        const sizeText = ($(this).attr('title') || '').trim();
        const variant = findFrameVariant(sizeText, color);
        
        if (variant) {
          totalPrice += (variant.price * qty);
          totalCompare += (variant.compare * qty);
        }
      });

      // Update price display
      $item.find('.mrk-frame-price').html(Shopify.formatMoney(totalPrice));

      // Update compare price if applicable
      if (totalCompare > totalPrice) {
        $item.find('.mrk-frame-compare-price')
          .html(Shopify.formatMoney(totalCompare))
          .show();
      } else {
        $item.find('.mrk-frame-compare-price')
          .empty()
          .hide();
      }
    });
  }

  // ========================================================================
  // ITEM UPDATE LOGIC
  // ========================================================================

  /**
   * Update a single gallery wall item with current selections
   */
  function updateItem($item) {
    const $sizeSel = $item.find('.mrk-variant-option').eq(0);
    const isFramingServiceChecked = $('[name="framing-service"]').is(':checked');
    const selectedSize = $item.find('.mrk-option-option').val();
    const $selectedFrame = $('.mrk-frame-switcher input:checked');
    const frameValue = $selectedFrame.val();
    
    let mainVariantId = null;

    // Select appropriate option based on framing service and frame
    if (isFramingServiceChecked) {
      // If framing service is checked, select by size only
      $sizeSel.find('option').each(function() {
        if ($(this).attr('title') === selectedSize) {
          $(this).prop('selected', true);
          return false; // break
        }
      });
    }

    // Find variant that matches size and frame
    $sizeSel.find('option').each(function() {
      const $option = $(this);
      const matchesSize = $option.attr('title') === selectedSize;
      const frameAttr = ($option.attr('frame') || '').toLowerCase();
      const matchesFrame = frameAttr.includes(frameValue.toLowerCase());
      
      if (matchesSize && matchesFrame) {
        if (!isFramingServiceChecked) {
          $option.prop('selected', true);
        }
        mainVariantId = $option.val();
        return false; // break
      }
    });

    // Get selected option details
    const $selectedOpt = $sizeSel.find(':selected');
    const mainVariant = {
      id: $sizeSel.val(),
      price: parseNumber($selectedOpt.attr('price')),
      compare_price: parseNumber($selectedOpt.attr('compare-price')),
      title: ($selectedOpt.attr('title') || '').trim()
    };

    // Extract size from title (content in parentheses) or use full title
    const sizeMatch = mainVariant.title.match(/\(([^)]+)\)/);
    const size = (sizeMatch ? sizeMatch[1] : mainVariant.title || '').trim();

    // Find frame variant
    const frameVariant = findFrameVariant(size, frameValue);
    const framePrice = parseNumber(frameVariant?.price || 0);
    const frameComparePrice = parseNumber(frameVariant?.compare || 0);
    const isUnframed = !frameVariant;

    // Get framing service details
    const $framingService = $('[name="framing-service"]');
    const hasFramingService = $framingService.is(':checked');
    const framingServiceVariant = $framingService.val();
    const framingServicePrice = parseNumber($framingService.data('price'));
    const framingServiceComparePrice = parseNumber($framingService.data('comparePrice'));

    // Build variants and prices arrays
    const variants = [];
    const prices = [];
    let sku = '';
    let frameSystemId = '';

    // Hide all frame images for this item
    $item.find('.mrk-frame-image-item').hide();

    if (!isUnframed) {
      // Add frame variant if framing service is checked
      if (isFramingServiceChecked) {
        variants.push({ 
          id: frameVariant.id, 
          parent_id: mainVariant.id 
        });
        prices.push({ 
          price: framePrice, 
          compare_price: frameComparePrice 
        });
      }

      // Show matching frame image
      const colorKey = (frameValue || '').toLowerCase();
      const $frameImage = $item.find(`.mrk-frame-image-item[color="${colorKey}"]`);
      
      if ($frameImage.length) {
        $frameImage.show();
        frameSystemId = $frameImage.attr('data-frame-id') || '';
      }

      // Build SKU
      sku = `${size}-${(frameValue || '').toString().toUpperCase()}-FRAME`;

      // Add framing service if applicable
      if (!hasFramingService && framingServiceVariant) {
        variants.push({ 
          id: framingServiceVariant, 
          parent_id: mainVariant.id 
        });
        prices.push({ 
          price: framingServicePrice, 
          compare_price: framingServiceComparePrice 
        });
        sku = `${size}-FRAMED-${(frameValue || '').toString().toUpperCase()}`;
      }

      // Enable frame ID input and show framing service UI
      $item.find('.mrk-frame-id')
        .val(frameSystemId)
        .prop('disabled', false);
      $('.mrk-framing-service-wrapper').show();
      if(!window.enable_remove_framing_service)
        $('.mrk-framing-service-wrapper').addClass('not-selectable');
        $('.mrk-framing-service-description').text('Framing services cannot be removed from this product. Your prints will be professionally framed before they are shipped, so they arrive ready to hang');
    } else {
      // Unframed: hide service UI and disable frame ID
      $('.mrk-framing-service-wrapper').hide();
      $item.find('.mrk-frame-id').prop('disabled', true);
    }

    // Calculate totals
    const addedPrice = prices.reduce((sum, p) => sum + parseNumber(p.price), 0);
    const addedCompare = prices.reduce((sum, p) => sum + parseNumber(p.compare_price), 0);
    const totalPrice = parseNumber(mainVariant.price) + addedPrice;
    const totalCompare = parseNumber(mainVariant.compare_price) + addedCompare;

    // Store all data on the item element
    $item.attr({
      'data-main-id': mainVariant.id || '',
      'data-frame-id': frameVariant?.id || '',
      'data-framing-service-id': (!isUnframed && !hasFramingService && framingServiceVariant) ? framingServiceVariant : '',
      'data-main-variant': mainVariantId || '',
      'data-frame-system-id': frameSystemId || '',
      'data-price': totalPrice,
      'data-compare': totalCompare,
      'data-sku': sku
    });

    $item.data({
      'variants': variants,
      'prices': prices,
      'mrk_sku': sku,
      'is_unframed': !!isUnframed
    });
  }

  // ========================================================================
  // FORM REBUILD
  // ========================================================================

  /**
   * Rebuild hidden form inputs and update totals
   */
  function rebuildForm() {
    const $inputsBox = $('.mrk-hidden-inputs');
    let inputs = '';
    let idx = 0;
    let grandTotal = 0;
    let grandCompare = 0;

    $('.mrk-gallery-wall-item').each(function() {
      const $item = $(this);

      // Ensure item is up-to-date
      if (!$item.attr('data-main-id')) {
        updateItem($item);
      }

      const mainId = $item.attr('data-main-id') || '';
      if (!mainId) return; // Skip invalid items

      const frameSystemId = $item.attr('data-frame-system-id') || '';
      const price = parseNumber($item.attr('data-price'));
      const compare = parseNumber($item.attr('data-compare'));
      const mainVariantId = parseNumber($item.attr('data-main-variant'));

      // Get prepared variants
      const preparedVariants = Array.isArray($item.data('variants')) 
        ? [...$item.data('variants')] 
        : [];

      // Add framing service if not already included
      const framingServiceId = $item.attr('data-framing-service-id');
      if (framingServiceId && !preparedVariants.some(v => String(v.id) === String(framingServiceId))) {
        preparedVariants.push({ 
          id: framingServiceId, 
          parent_id: mainId 
        });
      }

      // Build lines array: main item first, then children
      const lines = [
        { 
          id: mainId, 
          parent_id: '', 
          _frame_id: frameSystemId, 
          _main_variant: mainVariantId 
        },
        ...preparedVariants.map(v => ({
          id: v.id,
          parent_id: v.parent_id || mainId
        }))
      ];

      // Generate input fields
      lines.forEach(line => {
        if (idx === -1) {
          // First line in entire form uses name="id"
          inputs += `<input name="id" type="hidden" value="${line.id || ''}">\n`;
          
          if (line._frame_id) {
            inputs += `<input name="properties[_frame_id]" type="hidden" value="${line._frame_id}">\n`;
          }
          
          if (line._main_variant) {
            inputs += `<input name="properties[_main_variant]" type="hidden" value="${line._main_variant}">\n`;
          }
        } else {
          // Subsequent lines use items[idx]
          inputs += `<input name="items[${idx}][id]" type="hidden" value="${line.id || ''}">\n`;
          
          if (line.parent_id) {
            inputs += `<input name="items[${idx}][parent_id]" type="hidden" value="${line.parent_id}">\n`;
          }
          
          if (line._frame_id) {
            inputs += `<input name="items[${idx}][properties[_frame_id]]" type="hidden" value="${line._frame_id}">\n`;
          }
          
          if (line._main_variant) {
            inputs += `<input name="items[${idx}][properties[_main_variant]]" type="hidden" value="${line._main_variant}">\n`;
          }
        }
        
        idx++;
      });

      // Add to grand totals
      grandTotal += price;
      grandCompare += compare;
    });

    // Update DOM
    $inputsBox.html(inputs);
    $('.mrk-main-price').text(Shopify.formatMoney(grandTotal));
    $('.mrk-main-compare-price').text(grandCompare ? Shopify.formatMoney(grandCompare) : '');
    $('.mrk-discount-percent').text(calculateDiscount(grandTotal, grandCompare));

    // Update gallery frame display
    const $selectedFrame = $('frame-variant-picker input:checked').eq(1);
    const frameName = $selectedFrame.val()?.toLowerCase();
    
    if (frameName) {
      $('media-gallery .mrk-featured-product-frame-wrapper .mrk-frame-image-item')
        .removeClass('mrk-active-gallery-frame')
        .hide();
      
      $(`media-gallery .mrk-frame-image-item[color="${frameName}"]`)
        .addClass('mrk-active-gallery-frame')
        .show();
      
      $(`.mrk-tab-trigger[data-content="${frameName}"]`).trigger('click');
    }

    // Update frame totals
    updateFrameTotals();
  }

  // ========================================================================
  // EVENT HANDLERS
  // ========================================================================

  /**
   * Handle variant option changes within gallery items
   */
  $(document).on('change', '.mrk-gallery-wall-item .mrk-variant-option, .mrk-gallery-wall-item .mrk-option-option', function() {
    const $item = $(this).closest('.mrk-gallery-wall-item');
    updateItem($item);
    rebuildForm();
  });

  /**
   * Handle frame picker and framing service changes
   */
  $(document).on('change', 'frame-variant-picker, [name="framing-service"]', function() {
    $('.mrk-gallery-wall-item').each(function() {
      updateItem($(this));
    });
    rebuildForm();
  });

  /**
   * Handle custom variant changed event
   */
  $(document).on('mrkVariantChanged', function() {
    ensureFrameChecked();
  });

  // ========================================================================
  // INITIALIZATION
  // ========================================================================

  /**
   * Initialize on document ready
   */
  $(function() {
    // Ensure frame data is available
    window.mrkFrameData = window.mrkFrameData || [];

    // Update all items
    $('.mrk-gallery-wall-item').each(function() {
      updateItem($(this));
    });

    // Initial form rebuild
    rebuildForm();
  });

})(jQuery);